import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faEnvelope, faPhone, faUser, faTrash, faCalendarAlt, faSearch } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import api from '../utils/api';
import PropTypes from 'prop-types';

const ViewContacts = ({ darkMode, onDelete }) => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredContacts, setFilteredContacts] = useState([]);
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        show: false,
        contactId: null,
        contactName: '',
        isDeleting: false
    });

    useEffect(() => {
        fetchContacts();
    }, []);

    useEffect(() => {
        filterContacts();
    }, [contacts, searchTerm]);

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const response = await api.get('/auth/contacts');
            
            if (response.data.success) {
                setContacts(response.data.contacts);
            } else {
                setContacts([]);
            }
        } catch (error) {
            console.error('Error fetching contacts:', error);
            toast.error('Failed to load contacts');
            setContacts([]);
        } finally {
            setLoading(false);
        }
    };

    const filterContacts = () => {
        if (!searchTerm) {
            setFilteredContacts(contacts);
            return;
        }

        const filtered = contacts.filter(contact =>
            contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            contact.message.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredContacts(filtered);
    };

    const handleDeleteContact = (contactId) => {
        const contact = contacts.find(c => c._id === contactId);
        setDeleteConfirmation({
            show: true,
            contactId,
            contactName: contact.name,
            isDeleting: false
        });
    };

    const confirmDelete = async () => {
        setDeleteConfirmation(prev => ({ ...prev, isDeleting: true }));
        try {
            await api.delete(`/auth/contacts/${deleteConfirmation.contactId}`);
            setContacts(prev => prev.filter(contact => contact._id !== deleteConfirmation.contactId));
            setDeleteConfirmation({ show: false, contactId: null, contactName: '', isDeleting: false });
            toast.success('Contact deleted successfully!');
            onDelete && onDelete(deleteConfirmation.contactId);
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete contact');
            setDeleteConfirmation(prev => ({ ...prev, isDeleting: false }));
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

 

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Contact Messages
                </h2>
            </div>

            {/* Content Container */}
            <div className={`rounded-2xl border p-6 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } shadow-lg`}>
                <div className="space-y-6">
                    {/* Search Bar */}
                    <div className="mb-6">
                        <div className="relative">
                            <FontAwesomeIcon 
                                icon={faSearch} 
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
                            />
                            <input
                                type="text"
                                placeholder="Search contacts..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                                    darkMode 
                                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                        : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl mr-3" />
                            <span>Loading contacts...</span>
                        </div>
                    ) : filteredContacts.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="mb-4">
                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h8z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium mb-2">
                                {searchTerm ? 'No contacts found' : 'No contact messages yet'}
                            </h3>
                            <p className="text-gray-500">
                                {searchTerm ? 'Try adjusting your search criteria.' : 'Contact messages will appear here when visitors reach out.'}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="mb-4 flex items-center justify-between">
                                <h3 className="text-lg font-medium">
                                    {filteredContacts.length} Contact{filteredContacts.length !== 1 ? 's' : ''} Found
                                </h3>
                            </div>

                            <div className="space-y-4">
                                {filteredContacts.map((contact) => (
                                    <div
                                        key={contact._id}
                                        className={`p-6 rounded-lg border ${
                                            darkMode 
                                                ? 'bg-gray-700/50 border-gray-600' 
                                                : 'bg-white border-gray-200'
                                        } hover:shadow-lg transition-shadow`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div className={`p-2 rounded-full ${
                                                        darkMode ? 'bg-blue-900/50' : 'bg-blue-100'
                                                    }`}>
                                                        <FontAwesomeIcon 
                                                            icon={faUser} 
                                                            className={`text-sm ${
                                                                darkMode ? 'text-blue-300' : 'text-blue-600'
                                                            }`} 
                                                        />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-lg font-semibold">{contact.name}</h4>
                                                        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                                                            <span className="flex items-center gap-1">
                                                                <FontAwesomeIcon icon={faEnvelope} />
                                                                {contact.email}
                                                            </span>
                                                            {contact.phone && (
                                                                <span className="flex items-center gap-1">
                                                                    <FontAwesomeIcon icon={faPhone} />
                                                                    {contact.phone}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className="mb-4">
                                                    <h5 className="font-medium mb-2">Message:</h5>
                                                    <p className={`leading-relaxed ${
                                                        darkMode ? 'text-gray-300' : 'text-gray-600'
                                                    }`}>
                                                        {contact.message}
                                                    </p>
                                                </div>

                                                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                                                    <span className="flex items-center gap-1">
                                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                                        {formatDate(contact.createdAt)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="flex justify-end mt-4 pt-4 border-t border-gray-600/30">
                                            <button
                                                onClick={() => handleDeleteContact(contact._id)}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                                                    darkMode 
                                                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                                                        : 'bg-red-500 hover:bg-red-600 text-white'
                                                } transition-colors flex items-center gap-2`}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirmation.show && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className={`p-6 rounded-2xl shadow-xl max-w-md w-full border ${
                        darkMode ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 backdrop-blur-md border-gray-200'
                    }`}>
                        <h3 className="text-2xl font-semibold mb-2">Confirm Delete</h3>
                        <p className="mb-2">Are you sure you want to delete this contact message?</p>
                        <p className="mb-6 font-semibold">&quot;{deleteConfirmation.contactName}&quot;</p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setDeleteConfirmation({ show: false, contactId: null, contactName: '', isDeleting: false })}
                                className={`px-4 py-2 rounded ${
                                    darkMode ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'
                                }`}
                                disabled={deleteConfirmation.isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={deleteConfirmation.isDeleting}
                                className={`px-4 py-2 rounded text-white ${
                                    darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'
                                } disabled:bg-red-400 disabled:cursor-not-allowed flex items-center gap-2`}
                            >
                                {deleteConfirmation.isDeleting && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                                {deleteConfirmation.isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

ViewContacts.propTypes = {
    darkMode: PropTypes.bool.isRequired,
    onDelete: PropTypes.func
};

export default ViewContacts;
